import { useState } from 'react';
import { Logo } from '../components/Logo';
import { api, setToken } from '../api';
import type { AuthState } from '../types';

type Mode = 'login' | 'signup' | 'forgot-password' | 'reset-password';

interface Props {
  onLogin: (auth: AuthState) => void;
  initialMode?: Mode;
  resetToken?: string;
  onResetDone?: () => void;
  /** tier_1 | tier_2 | tier_3 — passed from App.tsx, read from ?plan= at mount time */
  signupTier?: string;
}

export function Login({ onLogin, initialMode = 'login', resetToken = '', onResetDone, signupTier }: Props) {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [showResend, setShowResend] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    try {
      if (mode === 'login') {
        const res = await api.auth.login(email, password);
        setToken(res.accessToken);
        onLogin({ accessToken: res.accessToken, email: res.user.email, tier: res.user.tier });
      } else if (mode === 'signup') {
        const res = await api.auth.signup(email, password, signupTier);
        if (res.stripeCheckoutUrl) {
          setToken(res.accessToken);
          localStorage.setItem('fixmyseo_access_token', res.accessToken);
          window.location.href = res.stripeCheckoutUrl;
          return;
        }
        setMessage('Account created — please sign in.');
        setMode('login');
      } else if (mode === 'forgot-password') {
        const res = await api.auth.forgotPassword(email);
        setMessage(res.message);
      } else if (mode === 'reset-password') {
        await api.auth.resetPassword(resetToken, newPassword);
        setMessage('Password updated — you can now sign in.');
        setTimeout(() => onResetDone?.(), 2000);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong';
      setError(msg);
      if (msg.toLowerCase().includes('not verified')) setShowResend(true);
    } finally {
      setLoading(false);
    }
  }

  const titles: Record<Mode, string> = {
    'login': 'Sign in',
    'signup': 'Create account',
    'forgot-password': 'Reset password',
    'reset-password': 'Choose new password',
  };

  return (
    <div className="min-h-screen bg-brand-ink flex flex-col items-center justify-center px-4">
      <div className="mb-10">
        <Logo size="md" />
      </div>
      <div className="w-full max-w-sm bg-brand-paper rounded-xl p-8 shadow-xl">
        <h1 className="text-brand-ink text-xl font-semibold mb-6">{titles[mode]}</h1>

        {message && (
          <p className="mb-4 text-sm text-brand-green bg-green-50 rounded-lg px-4 py-3">{message}</p>
        )}
        {error && (
          <div className="mb-4 text-sm text-red-600 bg-red-50 rounded-lg px-4 py-3">
            <p>{error}</p>
            {showResend && (
              <button
                type="button"
                onClick={async () => {
                  setShowResend(false);
                  setError('');
                  try {
                    const res = await api.auth.resendVerification(email);
                    setMessage(res.message);
                  } catch {
                    setError('Failed to resend — try again shortly');
                  }
                }}
                className="mt-2 underline font-medium block"
              >
                Resend verification email
              </button>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {(mode === 'login' || mode === 'signup' || mode === 'forgot-password') && (
            <div>
              <label className="block text-sm font-medium text-brand-ink mb-1" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-green"
              />
            </div>
          )}

          {(mode === 'login' || mode === 'signup') && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-brand-ink" htmlFor="password">
                  Password
                </label>
                {mode === 'login' && (
                  <button
                    type="button"
                    onClick={() => { setMode('forgot-password'); setError(''); setMessage(''); }}
                    className="text-xs text-gray-600 hover:text-brand-green-dark"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <input
                id="password"
                type="password"
                required
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-green"
              />
            </div>
          )}

          {mode === 'reset-password' && (
            <div>
              <label className="block text-sm font-medium text-brand-ink mb-1" htmlFor="new-password">
                New password
              </label>
              <input
                id="new-password"
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-green"
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-brand-green-dark text-white font-medium rounded-lg py-2.5 text-sm hover:bg-brand-green transition-colors disabled:opacity-50"
          >
            {loading
              ? 'Please wait…'
              : mode === 'login' ? 'Sign in'
              : mode === 'signup' ? 'Create account'
              : mode === 'forgot-password' ? 'Send reset link'
              : 'Update password'}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-600">
          {mode === 'login' && (
            <>
              No account?{' '}
              <button onClick={() => { setMode('signup'); setError(''); setMessage(''); }} className="text-brand-green-dark hover:underline">
                Sign up
              </button>
            </>
          )}
          {mode === 'signup' && (
            <>
              Already have an account?{' '}
              <button onClick={() => { setMode('login'); setError(''); setMessage(''); }} className="text-brand-green-dark hover:underline">
                Sign in
              </button>
            </>
          )}
          {(mode === 'forgot-password' || mode === 'reset-password') && (
            <button onClick={() => { setMode('login'); setError(''); setMessage(''); }} className="text-brand-green-dark hover:underline">
              Back to sign in
            </button>
          )}
        </p>
      </div>

      <p className="mt-8 text-xs text-gray-400 text-center max-w-xs">
        By signing in you agree to our{' '}
        <a href="https://fixmyseo.io/terms" className="underline">terms</a> and{' '}
        <a href="https://fixmyseo.io/privacy" className="underline">privacy policy</a>.
      </p>
    </div>
  );
}
